import { z } from 'zod';
import { Prisma } from '@prisma/humanex-client';

/////////////////////////////////////////
// HELPER FUNCTIONS
/////////////////////////////////////////

// JSON
//------------------------------------------------------

export type NullableJsonInput = Prisma.JsonValue | null | 'JsonNull' | 'DbNull' | Prisma.NullTypes.DbNull | Prisma.NullTypes.JsonNull;

export const transformJsonNull = (v?: NullableJsonInput) => {
  if (!v || v === 'DbNull') return Prisma.NullTypes.DbNull;
  if (v === 'JsonNull') return Prisma.NullTypes.JsonNull;
  return v;
};

export const JsonValueSchema: z.ZodType<Prisma.JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.literal(null),
    z.record(z.string(), z.lazy(() => JsonValueSchema.optional())),
    z.array(z.lazy(() => JsonValueSchema)),
  ])
);

export type JsonValueType = z.infer<typeof JsonValueSchema>;

export const NullableJsonValue = z
  .union([JsonValueSchema, z.literal('DbNull'), z.literal('JsonNull')])
  .nullable()
  .transform((v) => transformJsonNull(v));

export type NullableJsonValueType = z.infer<typeof NullableJsonValue>;

export const InputJsonValueSchema: z.ZodType<Prisma.InputJsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({ toJSON: z.any() }),
    z.record(z.string(), z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
    z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
  ])
);

export type InputJsonValueType = z.infer<typeof InputJsonValueSchema>;


/////////////////////////////////////////
// ENUMS
/////////////////////////////////////////

export enum TransactionIsolationLevel {ReadUncommitted = 'ReadUncommitted',ReadCommitted = 'ReadCommitted',RepeatableRead = 'RepeatableRead',Serializable = 'Serializable',}

export const TransactionIsolationLevelSchema = z.nativeEnum(TransactionIsolationLevel);

export type TransactionIsolationLevelType = z.infer<typeof TransactionIsolationLevelSchema>;

export enum AccountScalarFieldEnum {id = 'id',referralCode = 'referralCode',orgId = 'orgId',userId = 'userId',parentId = 'parentId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const AccountScalarFieldEnumSchema = z.nativeEnum(AccountScalarFieldEnum);

export type AccountScalarFieldEnumType = z.infer<typeof AccountScalarFieldEnumSchema>;

export enum ReferralStatsScalarFieldEnum {accountId = 'accountId',level1Count = 'level1Count',level1Earned = 'level1Earned',level1EarnPending = 'level1EarnPending',level2Count = 'level2Count',level2Earned = 'level2Earned',level2EarnPending = 'level2EarnPending',level3Count = 'level3Count',level3Earned = 'level3Earned',level3EarnPending = 'level3EarnPending',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ReferralStatsScalarFieldEnumSchema = z.nativeEnum(ReferralStatsScalarFieldEnum);

export type ReferralStatsScalarFieldEnumType = z.infer<typeof ReferralStatsScalarFieldEnumSchema>;

export enum UserRoleScalarFieldEnum {id = 'id',userId = 'userId',role = 'role',createdAt = 'createdAt',}

export const UserRoleScalarFieldEnumSchema = z.nativeEnum(UserRoleScalarFieldEnum);

export type UserRoleScalarFieldEnumType = z.infer<typeof UserRoleScalarFieldEnumSchema>;

export enum ActivationScalarFieldEnum {id = 'id',accountId = 'accountId',type = 'type',status = 'status',txId = 'txId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ActivationScalarFieldEnumSchema = z.nativeEnum(ActivationScalarFieldEnum);

export type ActivationScalarFieldEnumType = z.infer<typeof ActivationScalarFieldEnumSchema>;

export enum ApplicationScalarFieldEnum {id = 'id',candidateId = 'candidateId',programId = 'programId',referredById = 'referredById',status = 'status',depositAmount = 'depositAmount',depositPaid = 'depositPaid',reviewNotes = 'reviewNotes',supplementReq = 'supplementReq',submittedAt = 'submittedAt',reviewedAt = 'reviewedAt',txId = 'txId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ApplicationScalarFieldEnumSchema = z.nativeEnum(ApplicationScalarFieldEnum);

export type ApplicationScalarFieldEnumType = z.infer<typeof ApplicationScalarFieldEnumSchema>;

export enum ApplicationAnswerScalarFieldEnum {id = 'id',applicationId = 'applicationId',questionId = 'questionId',value = 'value',values = 'values',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ApplicationAnswerScalarFieldEnumSchema = z.nativeEnum(ApplicationAnswerScalarFieldEnum);

export type ApplicationAnswerScalarFieldEnumType = z.infer<typeof ApplicationAnswerScalarFieldEnumSchema>;

export enum AgentUserNoteScalarFieldEnum {id = 'id',agentId = 'agentId',candidateId = 'candidateId',note = 'note',internal = 'internal',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const AgentUserNoteScalarFieldEnumSchema = z.nativeEnum(AgentUserNoteScalarFieldEnum);

export type AgentUserNoteScalarFieldEnumType = z.infer<typeof AgentUserNoteScalarFieldEnumSchema>;

export enum CreditScalarFieldEnum {accountId = 'accountId',balance = 'balance',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditScalarFieldEnumSchema = z.nativeEnum(CreditScalarFieldEnum);

export type CreditScalarFieldEnumType = z.infer<typeof CreditScalarFieldEnumSchema>;

export enum CreditTxScalarFieldEnum {id = 'id',creditId = 'creditId',desc = 'desc',amount = 'amount',type = 'type',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxScalarFieldEnumSchema = z.nativeEnum(CreditTxScalarFieldEnum);

export type CreditTxScalarFieldEnumType = z.infer<typeof CreditTxScalarFieldEnumSchema>;

export enum CreditTxApplicationRewardMetaScalarFieldEnum {id = 'id',creditTxId = 'creditTxId',accountId = 'accountId',applicationId = 'applicationId',amount = 'amount',level = 'level',status = 'status',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxApplicationRewardMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxApplicationRewardMetaScalarFieldEnum);

export type CreditTxApplicationRewardMetaScalarFieldEnumType = z.infer<typeof CreditTxApplicationRewardMetaScalarFieldEnumSchema>;

export enum CreditTxPurchaseMetaScalarFieldEnum {creditTxId = 'creditTxId',orderCode = 'orderCode',linkId = 'linkId',method = 'method',status = 'status',currency = 'currency',qrCode = 'qrCode',checkoutUrl = 'checkoutUrl',expiresAt = 'expiresAt',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxPurchaseMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxPurchaseMetaScalarFieldEnum);

export type CreditTxPurchaseMetaScalarFieldEnumType = z.infer<typeof CreditTxPurchaseMetaScalarFieldEnumSchema>;

export enum CreditTxReferralRewardMetaScalarFieldEnum {creditTxId = 'creditTxId',referralId = 'referralId',level = 'level',status = 'status',creditTxUsageMetaId = 'creditTxUsageMetaId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxReferralRewardMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxReferralRewardMetaScalarFieldEnum);

export type CreditTxReferralRewardMetaScalarFieldEnumType = z.infer<typeof CreditTxReferralRewardMetaScalarFieldEnumSchema>;

export enum CreditTxUsageMetaScalarFieldEnum {creditTxId = 'creditTxId',bookingId = 'bookingId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxUsageMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxUsageMetaScalarFieldEnum);

export type CreditTxUsageMetaScalarFieldEnumType = z.infer<typeof CreditTxUsageMetaScalarFieldEnumSchema>;

export enum NewsletterScalarFieldEnum {id = 'id',email = 'email',isActive = 'isActive',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const NewsletterScalarFieldEnumSchema = z.nativeEnum(NewsletterScalarFieldEnum);

export type NewsletterScalarFieldEnumType = z.infer<typeof NewsletterScalarFieldEnumSchema>;

export enum OrgScalarFieldEnum {id = 'id',avatar = 'avatar',cover = 'cover',name = 'name',slug = 'slug',tagline = 'tagline',description = 'description',mission = 'mission',fee = 'fee',verified = 'verified',subscriptionPlan = 'subscriptionPlan',subscriptionExpiresAt = 'subscriptionExpiresAt',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgScalarFieldEnumSchema = z.nativeEnum(OrgScalarFieldEnum);

export type OrgScalarFieldEnumType = z.infer<typeof OrgScalarFieldEnumSchema>;

export enum OrgContactScalarFieldEnum {orgId = 'orgId',email = 'email',phone = 'phone',website = 'website',instagram = 'instagram',facebook = 'facebook',twitter = 'twitter',youtube = 'youtube',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgContactScalarFieldEnumSchema = z.nativeEnum(OrgContactScalarFieldEnum);

export type OrgContactScalarFieldEnumType = z.infer<typeof OrgContactScalarFieldEnumSchema>;

export enum OrgInvitationScalarFieldEnum {id = 'id',orgId = 'orgId',invitedById = 'invitedById',email = 'email',role = 'role',token = 'token',status = 'status',expiresAt = 'expiresAt',respondedAt = 'respondedAt',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgInvitationScalarFieldEnumSchema = z.nativeEnum(OrgInvitationScalarFieldEnum);

export type OrgInvitationScalarFieldEnumType = z.infer<typeof OrgInvitationScalarFieldEnumSchema>;

export enum OrgMemberScalarFieldEnum {id = 'id',orgId = 'orgId',userId = 'userId',role = 'role',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgMemberScalarFieldEnumSchema = z.nativeEnum(OrgMemberScalarFieldEnum);

export type OrgMemberScalarFieldEnumType = z.infer<typeof OrgMemberScalarFieldEnumSchema>;

export enum OrgReviewScalarFieldEnum {id = 'id',orgId = 'orgId',userId = 'userId',rating = 'rating',comment = 'comment',highlights = 'highlights',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgReviewScalarFieldEnumSchema = z.nativeEnum(OrgReviewScalarFieldEnum);

export type OrgReviewScalarFieldEnumType = z.infer<typeof OrgReviewScalarFieldEnumSchema>;

export enum OrgStatsScalarFieldEnum {orgId = 'orgId',rating = 'rating',totalReviews = 'totalReviews',ratingDistribution = 'ratingDistribution',totalEvents = 'totalEvents',avgAttendance = 'avgAttendance',eventsThisMonth = 'eventsThisMonth',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgStatsScalarFieldEnumSchema = z.nativeEnum(OrgStatsScalarFieldEnum);

export type OrgStatsScalarFieldEnumType = z.infer<typeof OrgStatsScalarFieldEnumSchema>;

export enum PartnerScalarFieldEnum {id = 'id',name = 'name',status = 'status',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const PartnerScalarFieldEnumSchema = z.nativeEnum(PartnerScalarFieldEnum);

export type PartnerScalarFieldEnumType = z.infer<typeof PartnerScalarFieldEnumSchema>;

export enum PartnerTokenScalarFieldEnum {id = 'id',partnerId = 'partnerId',accessToken = 'accessToken',refreshToken = 'refreshToken',accessExpiresAt = 'accessExpiresAt',refreshExpiresAt = 'refreshExpiresAt',createdAt = 'createdAt',}

export const PartnerTokenScalarFieldEnumSchema = z.nativeEnum(PartnerTokenScalarFieldEnum);

export type PartnerTokenScalarFieldEnumType = z.infer<typeof PartnerTokenScalarFieldEnumSchema>;

export enum PartnerUserScalarFieldEnum {id = 'id',partnerId = 'partnerId',userId = 'userId',pid = 'pid',status = 'status',linkedAt = 'linkedAt',lastSyncAt = 'lastSyncAt',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const PartnerUserScalarFieldEnumSchema = z.nativeEnum(PartnerUserScalarFieldEnum);

export type PartnerUserScalarFieldEnumType = z.infer<typeof PartnerUserScalarFieldEnumSchema>;

export enum AgentProfileScalarFieldEnum {userId = 'userId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const AgentProfileScalarFieldEnumSchema = z.nativeEnum(AgentProfileScalarFieldEnum);

export type AgentProfileScalarFieldEnumType = z.infer<typeof AgentProfileScalarFieldEnumSchema>;

export enum CandidateEducationScalarFieldEnum {userId = 'userId',educationLevel = 'educationLevel',schoolName = 'schoolName',graduationYear = 'graduationYear',academicResult = 'academicResult',vocationalCerts = 'vocationalCerts',languageSkills = 'languageSkills',computerSkill = 'computerSkill',driverLicenses = 'driverLicenses',updatedAt = 'updatedAt',}

export const CandidateEducationScalarFieldEnumSchema = z.nativeEnum(CandidateEducationScalarFieldEnum);

export type CandidateEducationScalarFieldEnumType = z.infer<typeof CandidateEducationScalarFieldEnumSchema>;

export enum CandidateFinancialScalarFieldEnum {userId = 'userId',tuitionAbility = 'tuitionAbility',accommodationSupport = 'accommodationSupport',participationBarriers = 'participationBarriers',updatedAt = 'updatedAt',}

export const CandidateFinancialScalarFieldEnumSchema = z.nativeEnum(CandidateFinancialScalarFieldEnum);

export type CandidateFinancialScalarFieldEnumType = z.infer<typeof CandidateFinancialScalarFieldEnumSchema>;

export enum CandidateGoalsScalarFieldEnum {userId = 'userId',primaryGoals = 'primaryGoals',preferredLocations = 'preferredLocations',industriesOfInterest = 'industriesOfInterest',targetCountries = 'targetCountries',expectedStart = 'expectedStart',expectedIncome = 'expectedIncome',preferredSchedule = 'preferredSchedule',currentlyEmployed = 'currentlyEmployed',currentMonthlyIncome = 'currentMonthlyIncome',documentsChecklist = 'documentsChecklist',updatedAt = 'updatedAt',}

export const CandidateGoalsScalarFieldEnumSchema = z.nativeEnum(CandidateGoalsScalarFieldEnum);

export type CandidateGoalsScalarFieldEnumType = z.infer<typeof CandidateGoalsScalarFieldEnumSchema>;

export enum CandidateHealthScalarFieldEnum {userId = 'userId',heightCm = 'heightCm',weightKg = 'weightKg',healthStatus = 'healthStatus',healthDetail = 'healthDetail',maritalStatus = 'maritalStatus',childrenCount = 'childrenCount',passportStatus = 'passportStatus',passportExpiry = 'passportExpiry',hasCriminalRecord = 'hasCriminalRecord',criminalRecordDetail = 'criminalRecordDetail',insuranceStatus = 'insuranceStatus',insuranceMonths = 'insuranceMonths',updatedAt = 'updatedAt',}

export const CandidateHealthScalarFieldEnumSchema = z.nativeEnum(CandidateHealthScalarFieldEnum);

export type CandidateHealthScalarFieldEnumType = z.infer<typeof CandidateHealthScalarFieldEnumSchema>;

export enum CandidateOverseasScalarFieldEnum {userId = 'userId',targetCertification = 'targetCertification',overseasExperience = 'overseasExperience',minContractDuration = 'minContractDuration',familyApprovalOverseas = 'familyApprovalOverseas',loanWillingness = 'loanWillingness',overseasNotes = 'overseasNotes',updatedAt = 'updatedAt',}

export const CandidateOverseasScalarFieldEnumSchema = z.nativeEnum(CandidateOverseasScalarFieldEnum);

export type CandidateOverseasScalarFieldEnumType = z.infer<typeof CandidateOverseasScalarFieldEnumSchema>;

export enum CandidatePersonalScalarFieldEnum {userId = 'userId',nationalId = 'nationalId',nationalIdIssueDate = 'nationalIdIssueDate',nationalIdIssuePlace = 'nationalIdIssuePlace',permanentAddress = 'permanentAddress',currentAddress = 'currentAddress',zaloId = 'zaloId',facebook = 'facebook',parentGuardian = 'parentGuardian',referralSource = 'referralSource',preferredContactChannels = 'preferredContactChannels',counselorNotes = 'counselorNotes',updatedAt = 'updatedAt',}

export const CandidatePersonalScalarFieldEnumSchema = z.nativeEnum(CandidatePersonalScalarFieldEnum);

export type CandidatePersonalScalarFieldEnumType = z.infer<typeof CandidatePersonalScalarFieldEnumSchema>;

export enum CandidateProfileScalarFieldEnum {userId = 'userId',nationalId = 'nationalId',address = 'address',educationLevel = 'educationLevel',userGroup = 'userGroup',careerGoal = 'careerGoal',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CandidateProfileScalarFieldEnumSchema = z.nativeEnum(CandidateProfileScalarFieldEnum);

export type CandidateProfileScalarFieldEnumType = z.infer<typeof CandidateProfileScalarFieldEnumSchema>;

export enum DocumentScalarFieldEnum {id = 'id',refType = 'refType',refId = 'refId',type = 'type',content = 'content',notes = 'notes',verified = 'verified',bgId = 'bgId',verifiedAt = 'verifiedAt',verifiedById = 'verifiedById',createdAt = 'createdAt',updatedAt = 'updatedAt',deletedAt = 'deletedAt',}

export const DocumentScalarFieldEnumSchema = z.nativeEnum(DocumentScalarFieldEnum);

export type DocumentScalarFieldEnumType = z.infer<typeof DocumentScalarFieldEnumSchema>;

export enum UserScalarFieldEnum {id = 'id',username = 'username',email = 'email',avatar = 'avatar',name = 'name',gender = 'gender',phone = 'phone',dateOfBirth = 'dateOfBirth',nationalId = 'nationalId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const UserScalarFieldEnumSchema = z.nativeEnum(UserScalarFieldEnum);

export type UserScalarFieldEnumType = z.infer<typeof UserScalarFieldEnumSchema>;

export enum ProgramScalarFieldEnum {id = 'id',slug = 'slug',orgId = 'orgId',featured = 'featured',status = 'status',activeRevisionId = 'activeRevisionId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ProgramScalarFieldEnumSchema = z.nativeEnum(ProgramScalarFieldEnum);

export type ProgramScalarFieldEnumType = z.infer<typeof ProgramScalarFieldEnumSchema>;

export enum ProgramQuestionScalarFieldEnum {id = 'id',programId = 'programId',type = 'type',label = 'label',options = 'options',required = 'required',order = 'order',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ProgramQuestionScalarFieldEnumSchema = z.nativeEnum(ProgramQuestionScalarFieldEnum);

export type ProgramQuestionScalarFieldEnumType = z.infer<typeof ProgramQuestionScalarFieldEnumSchema>;

export enum ProgramReviewScalarFieldEnum {id = 'id',revisionId = 'revisionId',adminId = 'adminId',action = 'action',note = 'note',reviewedAt = 'reviewedAt',}

export const ProgramReviewScalarFieldEnumSchema = z.nativeEnum(ProgramReviewScalarFieldEnum);

export type ProgramReviewScalarFieldEnumType = z.infer<typeof ProgramReviewScalarFieldEnumSchema>;

export enum ProgramRevisionScalarFieldEnum {id = 'id',programId = 'programId',version = 'version',title = 'title',description = 'description',banner = 'banner',type = 'type',country = 'country',location = 'location',quota = 'quota',requirements = 'requirements',benefits = 'benefits',costVisible = 'costVisible',depositAmount = 'depositAmount',deadline = 'deadline',status = 'status',submittedAt = 'submittedAt',submitNote = 'submitNote',reviewedAt = 'reviewedAt',reviewedBy = 'reviewedBy',reviewNote = 'reviewNote',publishedAt = 'publishedAt',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ProgramRevisionScalarFieldEnumSchema = z.nativeEnum(ProgramRevisionScalarFieldEnum);

export type ProgramRevisionScalarFieldEnumType = z.infer<typeof ProgramRevisionScalarFieldEnumSchema>;

export enum SortOrder {asc = 'asc',desc = 'desc',}

export const SortOrderSchema = z.nativeEnum(SortOrder);

export type SortOrderType = z.infer<typeof SortOrderSchema>;

export const JsonNullValueInputSchema = z.enum(['JsonNull',]).transform((value) => (value === 'JsonNull' ? Prisma.JsonNull : value));

export const NullableJsonNullValueInputSchema = z.enum(['DbNull','JsonNull',]).transform((value) => value === 'JsonNull' ? Prisma.JsonNull : value === 'DbNull' ? Prisma.DbNull : value);

export enum QueryMode {default = 'default',insensitive = 'insensitive',}

export const QueryModeSchema = z.nativeEnum(QueryMode);

export type QueryModeType = z.infer<typeof QueryModeSchema>;

export enum NullsOrder {first = 'first',last = 'last',}

export const NullsOrderSchema = z.nativeEnum(NullsOrder);

export type NullsOrderType = z.infer<typeof NullsOrderSchema>;

export const JsonNullValueFilterSchema = z.enum(['DbNull','JsonNull','AnyNull',]).transform((value) => value === 'JsonNull' ? Prisma.JsonNull : value === 'DbNull' ? Prisma.DbNull : value === 'AnyNull' ? Prisma.AnyNull : value);

export enum SystemRole {ADMIN = 'ADMIN',USER = 'USER',}

export const SystemRoleSchema = z.nativeEnum(SystemRole);

export type SystemRoleType = z.infer<typeof SystemRoleSchema>;

export enum ActivationType {AGENT = 'AGENT',CANDIDATE = 'CANDIDATE',ORG = 'ORG',}

export const ActivationTypeSchema = z.nativeEnum(ActivationType);

export type ActivationTypeType = z.infer<typeof ActivationTypeSchema>;

export enum ActivationStatus {INACTIVE = 'INACTIVE',ACTIVE = 'ACTIVE',SUSPENDED = 'SUSPENDED',}

export const ActivationStatusSchema = z.nativeEnum(ActivationStatus);

export type ActivationStatusType = z.infer<typeof ActivationStatusSchema>;

export enum ApplicationStatus {DEPOSIT_PENDING = 'DEPOSIT_PENDING',SUBMITTED = 'SUBMITTED',UNDER_REVIEW = 'UNDER_REVIEW',NEEDS_SUPPLEMENT = 'NEEDS_SUPPLEMENT',ACCEPTED = 'ACCEPTED',REJECTED = 'REJECTED',CANCELLED = 'CANCELLED',}

export const ApplicationStatusSchema = z.nativeEnum(ApplicationStatus);

export type ApplicationStatusType = z.infer<typeof ApplicationStatusSchema>;

export enum CreditTxTypeEnum {PURCHASE = 'PURCHASE',USAGE = 'USAGE',REFERRAL = 'REFERRAL',BONUS = 'BONUS',PAYMENT = 'PAYMENT',ACTIVATION = 'ACTIVATION',APPLICATION = 'APPLICATION',APPLICATION_REWARD = 'APPLICATION_REWARD',}

export const CreditTxTypeEnumSchema = z.nativeEnum(CreditTxTypeEnum);

export type CreditTxTypeEnumType = z.infer<typeof CreditTxTypeEnumSchema>;

export enum PurchaseMethodEnum {CREDIT_CARD = 'CREDIT_CARD',BANK_TRANSFER = 'BANK_TRANSFER',}

export const PurchaseMethodEnumSchema = z.nativeEnum(PurchaseMethodEnum);

export type PurchaseMethodEnumType = z.infer<typeof PurchaseMethodEnumSchema>;

export enum PurchaseStatusEnum {PENDING = 'PENDING',SUCCESS = 'SUCCESS',FAILED = 'FAILED',}

export const PurchaseStatusEnumSchema = z.nativeEnum(PurchaseStatusEnum);

export type PurchaseStatusEnumType = z.infer<typeof PurchaseStatusEnumSchema>;

export enum ReferralRewardStatus {PENDING = 'PENDING',RECEIVED = 'RECEIVED',CANCELLED = 'CANCELLED',}

export const ReferralRewardStatusSchema = z.nativeEnum(ReferralRewardStatus);

export type ReferralRewardStatusType = z.infer<typeof ReferralRewardStatusSchema>;

export enum OrgInvitationStatus {PENDING = 'PENDING',ACCEPTED = 'ACCEPTED',EXPIRED = 'EXPIRED',}

export const OrgInvitationStatusSchema = z.nativeEnum(OrgInvitationStatus);

export type OrgInvitationStatusType = z.infer<typeof OrgInvitationStatusSchema>;

export enum OrgMemberRole {MEMBER = 'MEMBER',REVIEWER = 'REVIEWER',ADMIN = 'ADMIN',OWNER = 'OWNER',}

export const OrgMemberRoleSchema = z.nativeEnum(OrgMemberRole);

export type OrgMemberRoleType = z.infer<typeof OrgMemberRoleSchema>;

export enum PartnerStatus {ACTIVE = 'ACTIVE',INACTIVE = 'INACTIVE',}

export const PartnerStatusSchema = z.nativeEnum(PartnerStatus);

export type PartnerStatusType = z.infer<typeof PartnerStatusSchema>;

export enum PartnerUserStatus {ACTIVE = 'ACTIVE',REVOKED = 'REVOKED',}

export const PartnerUserStatusSchema = z.nativeEnum(PartnerUserStatus);

export type PartnerUserStatusType = z.infer<typeof PartnerUserStatusSchema>;

export enum CandidateEducationLevel {HIGH_SCHOOL_STUDENT = 'HIGH_SCHOOL_STUDENT',HIGH_SCHOOL_GRAD = 'HIGH_SCHOOL_GRAD',VOCATIONAL = 'VOCATIONAL',COLLEGE = 'COLLEGE',UNIVERSITY = 'UNIVERSITY',VETERAN = 'VETERAN',NO_DIPLOMA = 'NO_DIPLOMA',}

export const CandidateEducationLevelSchema = z.nativeEnum(CandidateEducationLevel);

export type CandidateEducationLevelType = z.infer<typeof CandidateEducationLevelSchema>;

export enum CandidateHealthStatus {NORMAL = 'NORMAL',VISION_ISSUE = 'VISION_ISSUE',CHRONIC_DISEASE = 'CHRONIC_DISEASE',POST_SURGERY = 'POST_SURGERY',}

export const CandidateHealthStatusSchema = z.nativeEnum(CandidateHealthStatus);

export type CandidateHealthStatusType = z.infer<typeof CandidateHealthStatusSchema>;

export enum CandidateMaritalStatus {SINGLE = 'SINGLE',MARRIED = 'MARRIED',HAS_CHILDREN = 'HAS_CHILDREN',}

export const CandidateMaritalStatusSchema = z.nativeEnum(CandidateMaritalStatus);

export type CandidateMaritalStatusType = z.infer<typeof CandidateMaritalStatusSchema>;

export enum CandidatePassportStatus {VALID = 'VALID',IN_PROGRESS = 'IN_PROGRESS',NONE = 'NONE',}

export const CandidatePassportStatusSchema = z.nativeEnum(CandidatePassportStatus);

export type CandidatePassportStatusType = z.infer<typeof CandidatePassportStatusSchema>;

export enum DocumentType {NATIONAL_ID = 'NATIONAL_ID',PASSPORT = 'PASSPORT',TAX_ID = 'TAX_ID',BUSINESS_LICENSE = 'BUSINESS_LICENSE',CONTRACT = 'CONTRACT',}

export const DocumentTypeSchema = z.nativeEnum(DocumentType);

export type DocumentTypeType = z.infer<typeof DocumentTypeSchema>;

export enum DocumentRefType {USER = 'USER',ORG = 'ORG',PROGRAM = 'PROGRAM',}

export const DocumentRefTypeSchema = z.nativeEnum(DocumentRefType);

export type DocumentRefTypeType = z.infer<typeof DocumentRefTypeSchema>;

export enum Gender {MALE = 'MALE',FEMALE = 'FEMALE',OTHER = 'OTHER',PREFER_NOT_TO_SAY = 'PREFER_NOT_TO_SAY',}

export const GenderSchema = z.nativeEnum(Gender);

export type GenderType = z.infer<typeof GenderSchema>;

export enum ProfileStatus {ACTIVE = 'ACTIVE',INACTIVE = 'INACTIVE',BANNED = 'BANNED',}

export const ProfileStatusSchema = z.nativeEnum(ProfileStatus);

export type ProfileStatusType = z.infer<typeof ProfileStatusSchema>;

export enum ProgramType {DU_HOC = 'DU_HOC',VIEC_LAM = 'VIEC_LAM',XUAT_KHAU = 'XUAT_KHAU',HOC_NGHE = 'HOC_NGHE',}

export const ProgramTypeSchema = z.nativeEnum(ProgramType);

export type ProgramTypeType = z.infer<typeof ProgramTypeSchema>;

export enum ProgramStatus {DRAFT = 'DRAFT',PENDING_REVIEW = 'PENDING_REVIEW',REJECTED = 'REJECTED',VERIFIED = 'VERIFIED',PUBLISHED = 'PUBLISHED',PAUSED = 'PAUSED',CLOSED = 'CLOSED',}

export const ProgramStatusSchema = z.nativeEnum(ProgramStatus);

export type ProgramStatusType = z.infer<typeof ProgramStatusSchema>;

export enum ProgramRevisionStatus {DRAFT = 'DRAFT',PENDING_REVIEW = 'PENDING_REVIEW',APPROVED = 'APPROVED',REJECTED = 'REJECTED',SUPERSEDED = 'SUPERSEDED',}

export const ProgramRevisionStatusSchema = z.nativeEnum(ProgramRevisionStatus);

export type ProgramRevisionStatusType = z.infer<typeof ProgramRevisionStatusSchema>;

export enum ReviewAction {APPROVE = 'APPROVE',REJECT = 'REJECT',}

export const ReviewActionSchema = z.nativeEnum(ReviewAction);

export type ReviewActionType = z.infer<typeof ReviewActionSchema>;

export enum ProgramQuestionType {TEXT = 'TEXT',SINGLE_CHOICE = 'SINGLE_CHOICE',MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',PROFILE_FIELD = 'PROFILE_FIELD',FILE_UPLOAD = 'FILE_UPLOAD',}

export const ProgramQuestionTypeSchema = z.nativeEnum(ProgramQuestionType);

export type ProgramQuestionTypeType = z.infer<typeof ProgramQuestionTypeSchema>;
/////////////////////////////////////////
// MODELS
/////////////////////////////////////////

/////////////////////////////////////////
// ACCOUNT SCHEMA
/////////////////////////////////////////

export const AccountSchema = z.object({
  id: z.string(),
  referralCode: z.string(),
  orgId: z.string().nullable(),
  userId: z.string().nullable(),
  parentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Account = z.infer<typeof AccountSchema>

/////////////////////////////////////////
// REFERRAL STATS SCHEMA
/////////////////////////////////////////

export const ReferralStatsSchema = z.object({
  accountId: z.string(),
  level1Count: z.number().int(),
  level1Earned: z.number(),
  level1EarnPending: z.number(),
  level2Count: z.number().int(),
  level2Earned: z.number(),
  level2EarnPending: z.number(),
  level3Count: z.number().int(),
  level3Earned: z.number(),
  level3EarnPending: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type ReferralStats = z.infer<typeof ReferralStatsSchema>

/////////////////////////////////////////
// USER ROLE SCHEMA
/////////////////////////////////////////

export const UserRoleSchema = z.object({
  role: SystemRoleSchema,
  id: z.string().cuid(),
  userId: z.string(),
  createdAt: z.coerce.date(),
})

export type UserRole = z.infer<typeof UserRoleSchema>

/////////////////////////////////////////
// ACTIVATION SCHEMA
/////////////////////////////////////////

export const ActivationSchema = z.object({
  type: ActivationTypeSchema,
  status: ActivationStatusSchema,
  id: z.string().cuid(),
  accountId: z.string(),
  txId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Activation = z.infer<typeof ActivationSchema>

/////////////////////////////////////////
// APPLICATION SCHEMA
/////////////////////////////////////////

export const ApplicationSchema = z.object({
  status: ApplicationStatusSchema,
  id: z.string().cuid(),
  candidateId: z.string(),
  programId: z.string(),
  referredById: z.string().nullable(),
  depositAmount: z.number(),
  depositPaid: z.boolean(),
  reviewNotes: z.string().nullable(),
  supplementReq: z.string().nullable(),
  submittedAt: z.coerce.date().nullable(),
  reviewedAt: z.coerce.date().nullable(),
  txId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Application = z.infer<typeof ApplicationSchema>

/////////////////////////////////////////
// APPLICATION ANSWER SCHEMA
/////////////////////////////////////////

export const ApplicationAnswerSchema = z.object({
  id: z.string().cuid(),
  applicationId: z.string(),
  questionId: z.string(),
  value: z.string(),
  values: z.string().array(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type ApplicationAnswer = z.infer<typeof ApplicationAnswerSchema>

/////////////////////////////////////////
// AGENT USER NOTE SCHEMA
/////////////////////////////////////////

export const AgentUserNoteSchema = z.object({
  id: z.string().cuid(),
  agentId: z.string(),
  candidateId: z.string(),
  note: z.string(),
  internal: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type AgentUserNote = z.infer<typeof AgentUserNoteSchema>

/////////////////////////////////////////
// CREDIT SCHEMA
/////////////////////////////////////////

export const CreditSchema = z.object({
  accountId: z.string(),
  balance: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Credit = z.infer<typeof CreditSchema>

/////////////////////////////////////////
// CREDIT TX SCHEMA
/////////////////////////////////////////

export const CreditTxSchema = z.object({
  type: CreditTxTypeEnumSchema,
  id: z.string().cuid(),
  creditId: z.string(),
  desc: z.string(),
  amount: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTx = z.infer<typeof CreditTxSchema>

/////////////////////////////////////////
// CREDIT TX APPLICATION REWARD META SCHEMA
/////////////////////////////////////////

export const CreditTxApplicationRewardMetaSchema = z.object({
  status: ReferralRewardStatusSchema,
  id: z.string().cuid(),
  creditTxId: z.string().nullable(),
  accountId: z.string(),
  applicationId: z.string(),
  amount: z.number(),
  level: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxApplicationRewardMeta = z.infer<typeof CreditTxApplicationRewardMetaSchema>

/////////////////////////////////////////
// CREDIT TX PURCHASE META SCHEMA
/////////////////////////////////////////

export const CreditTxPurchaseMetaSchema = z.object({
  method: PurchaseMethodEnumSchema,
  status: PurchaseStatusEnumSchema,
  creditTxId: z.string(),
  orderCode: z.string(),
  linkId: z.string(),
  currency: z.string(),
  qrCode: z.string(),
  checkoutUrl: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxPurchaseMeta = z.infer<typeof CreditTxPurchaseMetaSchema>

/////////////////////////////////////////
// CREDIT TX REFERRAL REWARD META SCHEMA
/////////////////////////////////////////

export const CreditTxReferralRewardMetaSchema = z.object({
  status: ReferralRewardStatusSchema,
  creditTxId: z.string(),
  referralId: z.string(),
  level: z.number().int(),
  creditTxUsageMetaId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxReferralRewardMeta = z.infer<typeof CreditTxReferralRewardMetaSchema>

/////////////////////////////////////////
// CREDIT TX USAGE META SCHEMA
/////////////////////////////////////////

export const CreditTxUsageMetaSchema = z.object({
  creditTxId: z.string(),
  bookingId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxUsageMeta = z.infer<typeof CreditTxUsageMetaSchema>

/////////////////////////////////////////
// NEWSLETTER SCHEMA
/////////////////////////////////////////

export const NewsletterSchema = z.object({
  id: z.string().cuid(),
  email: z.string(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Newsletter = z.infer<typeof NewsletterSchema>

/////////////////////////////////////////
// ORG SCHEMA
/////////////////////////////////////////

export const OrgSchema = z.object({
  id: z.string().cuid(),
  avatar: z.string(),
  cover: z.string(),
  name: z.string(),
  slug: z.string().nullable(),
  tagline: z.string(),
  description: z.string(),
  mission: z.string().nullable(),
  fee: z.number(),
  verified: z.boolean(),
  subscriptionPlan: z.string().nullable(),
  subscriptionExpiresAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Org = z.infer<typeof OrgSchema>

/////////////////////////////////////////
// ORG CONTACT SCHEMA
/////////////////////////////////////////

export const OrgContactSchema = z.object({
  orgId: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  instagram: z.string().nullable(),
  facebook: z.string().nullable(),
  twitter: z.string().nullable(),
  youtube: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgContact = z.infer<typeof OrgContactSchema>

/////////////////////////////////////////
// ORG INVITATION SCHEMA
/////////////////////////////////////////

export const OrgInvitationSchema = z.object({
  role: OrgMemberRoleSchema,
  status: OrgInvitationStatusSchema,
  id: z.string().cuid(),
  orgId: z.string(),
  invitedById: z.string(),
  email: z.string(),
  token: z.string(),
  expiresAt: z.coerce.date(),
  respondedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgInvitation = z.infer<typeof OrgInvitationSchema>

/////////////////////////////////////////
// ORG MEMBER SCHEMA
/////////////////////////////////////////

export const OrgMemberSchema = z.object({
  role: OrgMemberRoleSchema,
  id: z.string().cuid(),
  orgId: z.string(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgMember = z.infer<typeof OrgMemberSchema>

/////////////////////////////////////////
// ORG REVIEW SCHEMA
/////////////////////////////////////////

export const OrgReviewSchema = z.object({
  id: z.string().cuid(),
  orgId: z.string(),
  userId: z.string(),
  rating: z.number().int(),
  comment: z.string(),
  highlights: z.string().array(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgReview = z.infer<typeof OrgReviewSchema>

/////////////////////////////////////////
// ORG STATS SCHEMA
/////////////////////////////////////////

export const OrgStatsSchema = z.object({
  orgId: z.string(),
  rating: z.number(),
  totalReviews: z.number().int(),
  ratingDistribution: JsonValueSchema,
  totalEvents: z.number().int(),
  avgAttendance: z.number().int(),
  eventsThisMonth: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgStats = z.infer<typeof OrgStatsSchema>

/////////////////////////////////////////
// PARTNER SCHEMA
/////////////////////////////////////////

export const PartnerSchema = z.object({
  status: PartnerStatusSchema,
  id: z.string().cuid(),
  name: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Partner = z.infer<typeof PartnerSchema>

/////////////////////////////////////////
// PARTNER TOKEN SCHEMA
/////////////////////////////////////////

export const PartnerTokenSchema = z.object({
  id: z.string().cuid(),
  partnerId: z.string(),
  accessToken: z.string(),
  refreshToken: z.string(),
  accessExpiresAt: z.coerce.date(),
  refreshExpiresAt: z.coerce.date(),
  createdAt: z.coerce.date(),
})

export type PartnerToken = z.infer<typeof PartnerTokenSchema>

/////////////////////////////////////////
// PARTNER USER SCHEMA
/////////////////////////////////////////

export const PartnerUserSchema = z.object({
  status: PartnerUserStatusSchema,
  id: z.string().cuid(),
  partnerId: z.string(),
  userId: z.string(),
  pid: z.string().nullable(),
  linkedAt: z.coerce.date(),
  lastSyncAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type PartnerUser = z.infer<typeof PartnerUserSchema>

/////////////////////////////////////////
// AGENT PROFILE SCHEMA
/////////////////////////////////////////

export const AgentProfileSchema = z.object({
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type AgentProfile = z.infer<typeof AgentProfileSchema>

/////////////////////////////////////////
// CANDIDATE EDUCATION SCHEMA
/////////////////////////////////////////

export const CandidateEducationSchema = z.object({
  educationLevel: CandidateEducationLevelSchema.nullable(),
  userId: z.string(),
  schoolName: z.string().nullable(),
  graduationYear: z.number().int().nullable(),
  academicResult: z.string().nullable(),
  vocationalCerts: z.string().array(),
  languageSkills: JsonValueSchema.nullable(),
  computerSkill: z.string().nullable(),
  driverLicenses: z.string().array(),
  updatedAt: z.coerce.date(),
})

export type CandidateEducation = z.infer<typeof CandidateEducationSchema>

/////////////////////////////////////////
// CANDIDATE FINANCIAL SCHEMA
/////////////////////////////////////////

export const CandidateFinancialSchema = z.object({
  userId: z.string(),
  tuitionAbility: z.string().nullable(),
  accommodationSupport: z.string().nullable(),
  participationBarriers: z.string().array(),
  updatedAt: z.coerce.date(),
})

export type CandidateFinancial = z.infer<typeof CandidateFinancialSchema>

/////////////////////////////////////////
// CANDIDATE GOALS SCHEMA
/////////////////////////////////////////

export const CandidateGoalsSchema = z.object({
  userId: z.string(),
  primaryGoals: z.string().array(),
  preferredLocations: z.string().array(),
  industriesOfInterest: z.string().array(),
  targetCountries: z.string().array(),
  expectedStart: z.string().nullable(),
  expectedIncome: z.string().nullable(),
  preferredSchedule: z.string().nullable(),
  currentlyEmployed: z.boolean().nullable(),
  currentMonthlyIncome: z.number().int().nullable(),
  documentsChecklist: JsonValueSchema.nullable(),
  updatedAt: z.coerce.date(),
})

export type CandidateGoals = z.infer<typeof CandidateGoalsSchema>

/////////////////////////////////////////
// CANDIDATE HEALTH SCHEMA
/////////////////////////////////////////

export const CandidateHealthSchema = z.object({
  healthStatus: CandidateHealthStatusSchema.nullable(),
  maritalStatus: CandidateMaritalStatusSchema.nullable(),
  passportStatus: CandidatePassportStatusSchema.nullable(),
  userId: z.string(),
  heightCm: z.number().int().nullable(),
  weightKg: z.number().int().nullable(),
  healthDetail: z.string().nullable(),
  childrenCount: z.number().int().nullable(),
  passportExpiry: z.string().nullable(),
  hasCriminalRecord: z.boolean().nullable(),
  criminalRecordDetail: z.string().nullable(),
  insuranceStatus: z.string().nullable(),
  insuranceMonths: z.number().int().nullable(),
  updatedAt: z.coerce.date(),
})

export type CandidateHealth = z.infer<typeof CandidateHealthSchema>

/////////////////////////////////////////
// CANDIDATE OVERSEAS SCHEMA
/////////////////////////////////////////

export const CandidateOverseasSchema = z.object({
  userId: z.string(),
  targetCertification: z.string().nullable(),
  overseasExperience: z.string().nullable(),
  minContractDuration: z.string().nullable(),
  familyApprovalOverseas: z.string().nullable(),
  loanWillingness: z.string().nullable(),
  overseasNotes: z.string().nullable(),
  updatedAt: z.coerce.date(),
})

export type CandidateOverseas = z.infer<typeof CandidateOverseasSchema>

/////////////////////////////////////////
// CANDIDATE PERSONAL SCHEMA
/////////////////////////////////////////

export const CandidatePersonalSchema = z.object({
  userId: z.string(),
  nationalId: z.string().nullable(),
  nationalIdIssueDate: z.string().nullable(),
  nationalIdIssuePlace: z.string().nullable(),
  permanentAddress: JsonValueSchema.nullable(),
  currentAddress: JsonValueSchema.nullable(),
  zaloId: z.string().nullable(),
  facebook: z.string().nullable(),
  parentGuardian: JsonValueSchema.nullable(),
  referralSource: z.string().nullable(),
  preferredContactChannels: z.string().array(),
  counselorNotes: z.string().nullable(),
  updatedAt: z.coerce.date(),
})

export type CandidatePersonal = z.infer<typeof CandidatePersonalSchema>

/////////////////////////////////////////
// CANDIDATE PROFILE SCHEMA
/////////////////////////////////////////

export const CandidateProfileSchema = z.object({
  userId: z.string(),
  nationalId: z.string().nullable(),
  address: z.string().nullable(),
  educationLevel: z.string().nullable(),
  userGroup: z.string().nullable(),
  careerGoal: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CandidateProfile = z.infer<typeof CandidateProfileSchema>

/////////////////////////////////////////
// DOCUMENT SCHEMA
/////////////////////////////////////////

export const DocumentSchema = z.object({
  refType: DocumentRefTypeSchema,
  type: DocumentTypeSchema,
  id: z.string().cuid(),
  refId: z.string(),
  content: JsonValueSchema,
  notes: z.string().array(),
  verified: z.boolean(),
  bgId: z.string().nullable(),
  verifiedAt: z.coerce.date().nullable(),
  verifiedById: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable(),
})

export type Document = z.infer<typeof DocumentSchema>

/////////////////////////////////////////
// USER SCHEMA
/////////////////////////////////////////

export const UserSchema = z.object({
  gender: GenderSchema,
  id: z.string(),
  username: z.string(),
  email: z.string(),
  avatar: z.string().nullable(),
  name: z.string(),
  phone: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  nationalId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type User = z.infer<typeof UserSchema>

/////////////////////////////////////////
// PROGRAM SCHEMA
/////////////////////////////////////////

export const ProgramSchema = z.object({
  status: ProgramStatusSchema,
  id: z.string().cuid(),
  slug: z.string(),
  orgId: z.string(),
  featured: z.boolean(),
  activeRevisionId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Program = z.infer<typeof ProgramSchema>

/////////////////////////////////////////
// PROGRAM QUESTION SCHEMA
/////////////////////////////////////////

export const ProgramQuestionSchema = z.object({
  type: ProgramQuestionTypeSchema,
  id: z.string().cuid(),
  programId: z.string(),
  label: z.string(),
  options: z.string().array(),
  required: z.boolean(),
  order: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type ProgramQuestion = z.infer<typeof ProgramQuestionSchema>

/////////////////////////////////////////
// PROGRAM REVIEW SCHEMA
/////////////////////////////////////////

export const ProgramReviewSchema = z.object({
  action: ReviewActionSchema,
  id: z.string().cuid(),
  revisionId: z.string(),
  adminId: z.string(),
  note: z.string().nullable(),
  reviewedAt: z.coerce.date(),
})

export type ProgramReview = z.infer<typeof ProgramReviewSchema>

/////////////////////////////////////////
// PROGRAM REVISION SCHEMA
/////////////////////////////////////////

export const ProgramRevisionSchema = z.object({
  type: ProgramTypeSchema,
  status: ProgramRevisionStatusSchema,
  id: z.string().cuid(),
  programId: z.string(),
  version: z.number().int(),
  title: z.string(),
  description: z.string(),
  banner: z.string().nullable(),
  country: z.string().nullable(),
  location: z.string().nullable(),
  quota: z.number().int(),
  requirements: z.string().nullable(),
  benefits: z.string().nullable(),
  costVisible: z.boolean(),
  depositAmount: z.number(),
  deadline: z.coerce.date().nullable(),
  submittedAt: z.coerce.date().nullable(),
  submitNote: z.string().nullable(),
  reviewedAt: z.coerce.date().nullable(),
  reviewedBy: z.string().nullable(),
  reviewNote: z.string().nullable(),
  publishedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type ProgramRevision = z.infer<typeof ProgramRevisionSchema>
